// src/components/settings/UserTable.jsx
import React, { useState, useEffect } from "react"; // Import useState and useEffect
import UserRowActionMenu from "./UserRowActionMenu";
import "../../assets/styles/common.css";
import "./Settings.css"; // Keep this for specific settings tab styles

const UserTable = ({
  users,
  isLoading,
  error,
  openMenuId,
  actionMenuRefs,
  toggleRowActions,
  handleEditUser,
  handleDeleteUser,
  setOpenMenuId,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8; // Fixed at 8 items per page as requested

  // Calculate total pages
  const totalPages = Math.ceil(users.length / itemsPerPage);

  // Calculate the index of the first and last item for the current page
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = users.slice(indexOfFirstItem, indexOfLastItem);

  // Effect to reset page to 1 if the number of users changes,
  // preventing issues if the current page becomes empty or invalid
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages); // Go to the last page if current page becomes invalid
    } else if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1); // Reset to page 1 if no users
    }
  }, [users.length, totalPages, currentPage]); // Re-run if users count or totalPages changes

  // Handlers for changing pages
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Render loading and error states first
  if (isLoading) {
    return <p className="loading-message">Loading users...</p>;
  }

  if (error) {
    return <p className="error-message">{error}</p>;
  }

  return (
    <div className="table-container">
      <table className="user-table">
        <thead>
          <tr>
            <th>Username</th>
            <th>Email Address</th>
            <th>Full Name</th>
            <th>Address</th>
            <th className="actions-header">Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentUsers.length > 0 ? (
            currentUsers.map((user) => (
              <tr key={user.id}>
                <td>{user.username}</td>
                <td>{user.email}</td>
                <td>{user.fullName}</td>
                <td>{user.address}</td>
                <UserRowActionMenu
                  user={user}
                  openMenuId={openMenuId}
                  actionMenuRefs={actionMenuRefs}
                  toggleRowActions={toggleRowActions}
                  handleEditUser={handleEditUser}
                  handleDeleteUser={handleDeleteUser}
                  setOpenMenuId={setOpenMenuId}
                />
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" className="no-data">
                {/* Displayed if currentUsers is empty (e.g., after filter or no users at all) */}
                No users found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination Controls */}
      {totalPages > 1 && ( // Only show pagination if there's more than 1 page
        <div className="pagination">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            Previous
          </button>
          {/* Render page number buttons */}
          {[...Array(totalPages).keys()].map((number) => (
            <button
              key={number + 1}
              onClick={() => paginate(number + 1)}
              className={`pagination-button ${
                currentPage === number + 1 ? "active" : ""
              }`}
            >
              {number + 1}
            </button>
          ))}
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default UserTable;
